import { useAuthStore } from '@/stores/authStore';

// Map legacy IANA names that browsers may not recognize
const LEGACY_MAP = {
    'US/Samoa': 'Pacific/Pago_Pago',
    'US/East-Indiana': 'America/Indiana/Indianapolis',
    'Canada/Saskatchewan': 'America/Regina',
    'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
    'Greenland': 'America/Godthab',
    'Europe/Kiev': 'Europe/Kyiv',
    'Asia/Chongqing': 'Asia/Shanghai',
    'Australia/Canberra': 'Australia/Sydney',
};

function resolveTimezone(tz) {
    if (!tz) return undefined;
    return LEGACY_MAP[tz] || tz;
}

function getUserTimezone() {
    try {
        const authStore = useAuthStore();
        return resolveTimezone(authStore.user?.timezone);
    } catch {
        return undefined;
    }
}

// The COMPANY timezone (companies.timezone), falling back to the user timezone
// when the company has none. The /profile payload carries it on user.company.
function getCompanyTimezone() {
    try {
        const authStore = useAuthStore();
        return resolveTimezone(authStore.user?.company?.timezone) || getUserTimezone();
    } catch {
        return getUserTimezone();
    }
}

// Parse an API timestamp (UTC, often without a zone suffix) to a Date, or null.
export function parseApiDate(ts) {
    if (!ts) return null;
    let dateStr = ts instanceof Date ? ts : String(ts);
    if (typeof dateStr === 'string') {
        // Ensure UTC parsing: add 'Z' if no timezone indicator
        if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('T')) {
            dateStr = dateStr.replace(' ', 'T') + 'Z';
        } else if (dateStr.includes('T') && !dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-', dateStr.indexOf('T'))) {
            dateStr += 'Z';
        }
        dateStr = new Date(dateStr);
    }
    return isNaN(dateStr.getTime()) ? null : dateStr;
}

const DEFAULT_DT_OPTS = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true,
};

function formatInZone(ts, opts, tz) {
    const d = parseApiDate(ts);
    if (!d) return '';
    const formatOpts = { ...DEFAULT_DT_OPTS, ...opts };
    if (tz) formatOpts.timeZone = tz;
    try {
        return new Intl.DateTimeFormat('en-US', formatOpts).format(d);
    } catch {
        // Fallback if timezone is invalid
        return d.toLocaleString('en');
    }
}

/**
 * Format a UTC timestamp string to the user's timezone.
 * @param {string|Date} ts - UTC timestamp (string or Date)
 * @param {object} [opts] - Intl.DateTimeFormat options override
 * @returns {string} Formatted date/time string
 */
export function formatDateTime(ts, opts) {
    return formatInZone(ts, opts, getUserTimezone());
}

/**
 * Format a UTC timestamp in the COMPANY timezone (companies.timezone), falling
 * back to the user timezone when the company has none. Use this for anything
 * that must line up with how the server localizes alert times - alert and
 * last-communication timestamps - so web reads the same as the fleet app.
 */
export function formatCompanyDateTime(ts, opts) {
    return formatInZone(ts, opts, getCompanyTimezone());
}

/**
 * Format a UTC timestamp to short date only (e.g., "3/1/2026").
 */
export function formatDate(ts) {
    return formatDateTime(ts, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: undefined,
        minute: undefined,
        second: undefined,
    });
}

/**
 * Format a calendar date (YYYY-MM-DD) as M/D/YYYY WITHOUT timezone conversion.
 * Routing a bare date through formatDateTime appends 'Z', so UTC midnight
 * renders as the previous day in western timezones. Full timestamps fall
 * through to the timezone-aware formatter.
 */
export function formatDateOnly(ts) {
    if (!ts) return '';
    const m = String(ts).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return `${parseInt(m[2], 10)}/${parseInt(m[3], 10)}/${m[1]}`;
    return formatDateTime(ts);
}

/**
 * Format a local Date (e.g., `new Date()`) to user timezone.
 * For timestamps that are already local (like `new Date()`).
 */
export function formatLocalDateTime(date) {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '';

    const tz = getUserTimezone();
    const opts = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: true,
    };
    if (tz) opts.timeZone = tz;

    try {
        return new Intl.DateTimeFormat('en-US', opts).format(d);
    } catch {
        return d.toLocaleString('en');
    }
}

/**
 * The company timezone as an IANA name (falls back to the user's), or
 * undefined when neither is set and the browser's own zone applies.
 */
export function companyTimezone() {
    return getCompanyTimezone();
}

/**
 * Format a UTC timestamp in a named zone, such as the zone the API says a
 * fleet's data is cut in. Falls back to the company zone when none is given.
 */
export function formatInTimezone(ts, opts, tz) {
    return formatInZone(ts, opts, resolveTimezone(tz) || getCompanyTimezone());
}

/**
 * A formatter for many timestamps in the company zone, such as chart labels:
 * builds one Intl.DateTimeFormat instead of one per call.
 */
export function companyDateTimeFormatter(opts) {
    const formatOpts = { ...DEFAULT_DT_OPTS, ...opts };
    const tz = getCompanyTimezone();
    if (tz) formatOpts.timeZone = tz;
    let fmt;
    try {
        fmt = new Intl.DateTimeFormat('en-US', formatOpts);
    } catch {
        delete formatOpts.timeZone;
        fmt = new Intl.DateTimeFormat('en-US', formatOpts);
    }
    return (ts) => {
        const d = parseApiDate(ts);
        return d ? fmt.format(d) : '';
    };
}

/** A zone as people say it ("Central Daylight Time"), or its IANA name. */
export function timezoneLongName(tz) {
    const zone = resolveTimezone(tz);
    try {
        const part = new Intl.DateTimeFormat('en-US', {timeZone: zone, timeZoneName: 'long'})
            .formatToParts(new Date())
            .find((p) => p.type === 'timeZoneName');
        return part?.value || zone || "your browser's timezone";
    } catch {
        return zone || "your browser's timezone";
    }
}

/**
 * A UTC timestamp as the value of an <input type="datetime-local">
 * (YYYY-MM-DDTHH:mm) on the wall clock of `tz`.
 */
export function toZonedInput(ts, tz) {
    const d = parseApiDate(ts);
    if (!d) return '';
    const parts = zonedParts(d, resolveTimezone(tz));
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

/**
 * The reverse of toZonedInput: a wall-clock YYYY-MM-DDTHH:mm in `tz`, as a UTC
 * ISO string, or null when it cannot be read. Resolved by measuring the zone's
 * offset at that moment, twice, so a time next to a DST change lands right.
 */
export function zonedInputToUtcIso(value, tz) {
    const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    if (!m) return null;
    const asUtc = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
    const zone = resolveTimezone(tz);
    const offsetAt = (ms) => {
        const p = zonedParts(new Date(ms), zone);
        return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute) - ms;
    };
    let guess = asUtc - offsetAt(asUtc);
    guess = asUtc - offsetAt(guess);
    return new Date(guess).toISOString();
}

function zonedParts(d, tz) {
    const opts = {year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'};
    if (tz) opts.timeZone = tz;
    let fmt;
    try {
        fmt = new Intl.DateTimeFormat('en-US', opts);
    } catch {
        delete opts.timeZone;
        fmt = new Intl.DateTimeFormat('en-US', opts);
    }
    const out = {};
    for (const p of fmt.formatToParts(d)) out[p.type] = p.value;
    // Some engines print midnight as 24 even with h23.
    if (out.hour === '24') out.hour = '00';
    return out;
}

/**
 * The calendar day (YYYY-MM-DD) a UTC timestamp falls on in the company
 * timezone, or '' when it cannot be read. Use it to group rows by day so the
 * groups agree with the company-zone times printed on them.
 */
export function companyDayKey(ts) {
    const d = parseApiDate(ts);
    if (!d) return '';
    const p = zonedParts(d, getCompanyTimezone());
    return `${p.year}-${p.month}-${p.day}`;
}

/** A YYYY-MM-DD day key moved by `days` calendar days. */
export function shiftDayKey(key, days) {
    const m = String(key || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return '';
    return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3] + days)).toISOString().slice(0, 10);
}

export function useTimezone() {
    return { formatDateTime, formatCompanyDateTime, formatDate, formatLocalDateTime };
}
